// ========================================
// CENTRAL CONFIGURATION OF ANIMATIONS
// ========================================
// This file controls all the timings and parameters of the animation
// Modify these values to adjust the behavior

export const animationConfig = ({
  text1,
  text2,
  waitlistText,
  learnMoreText,
}: {
  text1: string
  text2: string
  waitlistText: string
  learnMoreText: string
}) => ({
  // ----------------------------
  // DIMENSIONS OF THE TOGGLE
  // ----------------------------
  toggle: {
    height: 94,
    padding: 16,
    borderRadius: 100,
    // Animation spring configuration
    spring: {
      stiffness: 120,
      damping: 20,
    },
  },

  // ----------------------------
  // ANIMATIONS OF THE LOGO
  // ----------------------------
  logo: {
    // Rotation in each step (in degrees)
    rotateStep1: 180, // 1 complete turn in the first movement
    rotateStep2: 360, // 2 complete turns in the second movement
    rotateStep3: 540, // 3 complete turns in the third movement

    // Duration of each movement (in seconds)
    durationStep1: 0.6, // Duration of the first movement
    durationStep2: 0.6, // Duration of the second movement
    durationStep3: 0.8, // Duration of the third movement

    // Type of easing for the movement
    easing: 'easeInOut',

    // When the logo changes to white (fraction of the movement)
    colorChangePoint: 1 / 12, // A 1/3 of the first movement (333ms if duration is 1s)
  },

  // ----------------------------
  // GENERAL TIMINGS
  // ----------------------------
  timings: {
    // Delay before starting everything
    initialFadeInDelay: 500,

    // Pauses between steps
    step1Pause: 2500, // Pause after the first step (2.5 seconds)
    step2Pause: 1000, // Pause after the second step (1 second) - reduced for 7s total duration
    // step3Pause no longer used - the animation stays in the last step

    // Timings of fade
    step1FadeOut: 200, // Time for fade out of the first text
    step2FadeOut: 200, // Time for fade out of the second text
    finalFadeOutDuration: 640, // Duration of the final fade out

    // Gradiente
    gradientFadeIn: 0.4, // Time for the gradient to appear
    logoColorFade: 0.1, // Time for the logo to change color

    // Gradient animated
    gradientRotationDuration: 8, // Duration of a complete rotation (seconds)

    // Typewriter
    typewriterSpeed: 0.4, // Duration of the total typewriter effect (seconds)
  },

  // ----------------------------
  // CONTENT
  // ----------------------------
  content: {
    texts: [text1, text2],
    buttons: [
      { text: waitlistText, action: 'waitlist', primary: true },
      { text: learnMoreText, action: 'landing', primary: false },
      { text: '×', action: 'close', primary: false },
    ],
    gradient:
      'linear-gradient(270deg, rgba(161, 173, 229, 0.7) 0%, rgba(229, 25, 67, 0.7) 50%, rgba(229, 86, 25, 0.7) 100%)',
  },

  // ----------------------------
  // DYNAMIC WIDTHS (AUTOMATIC)
  // ----------------------------
  // NOTE: The widths are calculated automatically by measuring the real text.
  // This works for any language and text length.
  widths: {
    // Initial width (type toggle)
    initial: 160,

    // Minimum security widths (now that the calculation is more precise, they can be smaller)
    minStep1Width: 250, // Minimum width for the first step
    minStep2Width: 320, // Minimum width for the second step
    minStep3Width: 300, // Minimum width for the third step
  },
})

// ----------------------------
// HELPER FUNCTIONS
// ----------------------------

// Check if fonts are loaded
const checkFontsLoaded = () => {
  if (document.fonts && document.fonts.ready) {
    return document.fonts.ready
  }
  // Fallback for older browsers
  return Promise.resolve()
}

// Wait for fonts to be loaded with a timeout
const waitForFonts = async (timeout = 3000) => {
  try {
    await Promise.race([
      checkFontsLoaded(),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Font loading timeout')), timeout)
      ),
    ])
  } catch (error) {
    // Font loading check failed or timed out, continue anyway
  }
}

// Function to measure the width of the text dynamically with exact styles
const measureTextWidth = async (
  text: string,
  fontSize = 22,
  fontWeight = 600,
  fontFamily = 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
  letterSpacing = '-1%',
  retryCount = 0,
  maxRetries = 3
): Promise<number> => {
  if (!text) return 0

  try {
    // Wait for fonts to be loaded before measuring
    await waitForFonts()

    // Wait for next frame to ensure DOM is ready
    await new Promise((resolve) => requestAnimationFrame(resolve))

    // Create a temporary element with exact same styling as the component
    const tempElement = document.createElement('div')
    tempElement.style.cssText = `
      position: absolute;
      top: -9999px;
      left: -9999px;
      visibility: hidden;
      white-space: nowrap;
      font-family: ${fontFamily};
      font-size: ${fontSize}px;
      font-weight: ${fontWeight};
      font-style: normal;
      line-height: 28px;
      letter-spacing: ${letterSpacing};
      pointer-events: none;
    `
    tempElement.textContent = text

    document.body.appendChild(tempElement)

    // Force multiple reflows to ensure the element is fully rendered
    tempElement.offsetHeight
    tempElement.getBoundingClientRect().width

    // Wait one more frame
    await new Promise((resolve) => requestAnimationFrame(resolve))

    const width = tempElement.getBoundingClientRect().width
    document.body.removeChild(tempElement)

    // If width is 0 or very small, it might mean fonts aren't loaded yet
    if (width < 10 && retryCount < maxRetries) {
      return new Promise((resolve) => {
        setTimeout(async () => {
          const result = await measureTextWidth(
            text,
            fontSize,
            fontWeight,
            fontFamily,
            letterSpacing,
            retryCount + 1,
            maxRetries
          )
          resolve(result)
        }, 200 * (retryCount + 1)) // Increasing delay with each retry
      })
    }

    return Math.ceil(width)
  } catch (error) {
    // Return a fallback width based on text length
    return text.length * (fontSize * 0.6) // Rough estimation
  }
}

// Calculate the width for each step based on dynamic measurement
// IMPORTANT: We always reserve 78px to the right for the icon + right padding
export const getWidthForStep = async (
  step: number,
  ballSize: number,
  padding: number,
  texts: string[],
  config: ReturnType<typeof animationConfig>
) => {
  const ICON_RESERVED_SPACE = 78 // Fixed space reserved for icon + right padding
  const EXTRA_SPACING = 16 // Extra spacing to not be too close

  let result

  switch (step) {
    case 0:
      result = config.widths.initial
      break
    case 1: {
      const textWidth = await measureTextWidth(texts[0] || '', 22, 600, 'Inter', '-1%')
      const textMarginLeft = 32 // left margin of the text
      const calculatedWidth = textMarginLeft + textWidth + EXTRA_SPACING + ICON_RESERVED_SPACE
      result = Math.max(calculatedWidth, config.widths.minStep1Width)
      break
    }
    case 2: {
      const textWidth = await measureTextWidth(texts[1] || '', 22, 600, 'Inter', '-1%')
      const textMarginLeft = 32 // left margin of the text
      const calculatedWidth = textMarginLeft + textWidth + EXTRA_SPACING + ICON_RESERVED_SPACE
      result = Math.max(calculatedWidth, config.widths.minStep2Width)
      break
    }
    case 3: {
      // For the buttons, calculate the real width dynamically
      const buttons = config.content.buttons.filter((button) => button.action !== 'close')
      let totalButtonsWidth = 0

      for (const button of buttons) {
        // Calculate the approximate width of each button with the correct font
        const textWidth = await measureTextWidth(button.text, 16, 500, 'Inter') // fontSize 16, fontWeight 500, Inter font
        const buttonWidth = textWidth + 48 // padding 24px * 2 para botones más grandes
        totalButtonsWidth += buttonWidth
      }

      // Add gap between buttons (12px per gap)
      const gaps = (buttons.length - 1) * 12
      const buttonsMarginLeft = 16 // left margin of the buttons
      const buttonsWidth = totalButtonsWidth + gaps

      // Simplified calculation: left margin + buttons + extra spacing + space reserved for icon
      const calculatedWidth = buttonsMarginLeft + buttonsWidth + EXTRA_SPACING + ICON_RESERVED_SPACE
      result = Math.max(calculatedWidth, config.widths.minStep3Width)

      break
    }
    default:
      result = config.widths.initial
  }

  return result
}

// Calculate when the logo color should change
export const getLogoColorChangeTime = (config: ReturnType<typeof animationConfig>) => {
  return config.logo.durationStep1 * 1000 * config.logo.colorChangePoint
}
